import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import DeviceOnlineBadge from './DeviceOnlineBadge';
import SetupGuard from '../components/SetupGuard';
import './AdminAccount.css';

const VkiosksPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [kiosks, setKiosks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKiosk, setEditingKiosk] = useState(null);
  const [formKioskName, setFormKioskName] = useState('');
  const [formAcceptsCash, setFormAcceptsCash] = useState(true);
  const [formStandbyEnabled, setFormStandbyEnabled] = useState(true);
  const [formIdleTimeout, setFormIdleTimeout] = useState(5);
  const [formCheckoutTimeout, setFormCheckoutTimeout] = useState(2);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadData = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();

      // Load locations first
      const locationsSnapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locationsData = locationsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLocations(locationsData);

      // Determine selected location: read users/{uid} doc
      const userDoc = await getDoc(doc(db, 'users', uid));
      let activeLocId = null;
      if (userDoc.exists()) {
        activeLocId = userDoc.data().selectedLocationId || null;
      }
      // Fallback to first location if none selected
      if (!activeLocId && locationsData.length > 0) {
        activeLocId = locationsData[0].id;
      }
      setSelectedLocationId(activeLocId);

      // Load kiosks for selected location
      if (activeLocId) {
        const kiosksQuery = query(
          collection(db, 'users', uid, 'vkiosks'),
          where('locationId', '==', activeLocId),
          orderBy('createdAt', 'desc')
        );
        const kiosksSnapshot = await getDocs(kiosksQuery);
        const kiosksData = kiosksSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setKiosks(kiosksData);
      } else {
        setKiosks([]);
      }
    } catch (error) {
      console.error('Error loading kiosks:', error);
      Swal.fire('Error', 'Failed to load kiosks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingKiosk(null);
    setFormKioskName('');
    setFormAcceptsCash(true);
    setFormStandbyEnabled(true);
    setFormIdleTimeout(5);
    setFormCheckoutTimeout(2);
    setShowModal(true);
  };

  const openEditModal = (kiosk) => {
    setEditingKiosk(kiosk);
    setFormKioskName(kiosk.kioskName || '');
    setFormAcceptsCash(kiosk.acceptsCash ?? true);
    setFormStandbyEnabled(kiosk.standbyEnabled ?? true);
    setFormIdleTimeout(kiosk.idleTimeoutMinutes ?? 5);
    setFormCheckoutTimeout(kiosk.checkoutTimeoutMinutes ?? 2);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingKiosk(null);
    setFormKioskName('');
    setFormAcceptsCash(true);
    setFormStandbyEnabled(true);
    setFormIdleTimeout(5);
    setFormCheckoutTimeout(2);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!formKioskName.trim()) {
      Swal.fire('Error', 'Please enter a kiosk name', 'error');
      return;
    }
    if (!selectedLocationId) {
      Swal.fire('Error', 'No location selected', 'error');
      return;
    }

    try {
      const db = getFirestore();
      const idleTimeout = Math.max(1, Math.floor(Number(formIdleTimeout)) || 5);
      const checkoutTimeout = Math.max(1, Math.floor(Number(formCheckoutTimeout)) || 2);
      if (editingKiosk) {
        await updateDoc(doc(db, 'users', user.uid, 'vkiosks', editingKiosk.id), {
          kioskName: formKioskName.trim(),
          acceptsCash: formAcceptsCash,
          standbyEnabled: formStandbyEnabled,
          idleTimeoutMinutes: idleTimeout,
          checkoutTimeoutMinutes: checkoutTimeout,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'vkiosks')), {
          kioskName: formKioskName.trim(),
          locationId: selectedLocationId,
          acceptsCash: formAcceptsCash,
          standbyEnabled: formStandbyEnabled,
          idleTimeoutMinutes: idleTimeout,
          checkoutTimeoutMinutes: checkoutTimeout,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      closeModal();
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Kiosk ${editingKiosk ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving kiosk:', error);
      Swal.fire('Error', 'Failed to save kiosk', 'error');
    }
  };

  const handlePairDevice = async (kiosk) => {
    if (!user) return;
    const { value: code } = await Swal.fire({
      title: 'Pair Physical Device',
      input: 'text',
      inputLabel: 'Enter the activation code displayed on the device',
      inputPlaceholder: 'e.g. ABC123',
      showCancelButton: true,
      confirmButtonText: 'Pair',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });
    if (!code) return;

    try {
      setLoading(true);
      const callable = httpsCallable(getFunctions(), 'pairDeviceByActivationCode');
      const result = await callable({
        activationCode: code.trim().toUpperCase(),
        deviceType: 'kiosk',
        linkedDocId: kiosk.id,
      });
      if (result.data.success) {
        await loadData(user.uid);
        Swal.fire('Paired', 'Device paired successfully', 'success');
      }
    } catch (error) {
      console.error('Error pairing device:', error);
      Swal.fire('Error', error.message || 'Failed to pair device', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpairDevice = async (kiosk) => {
    if (!user) return;
    const result = await Swal.fire({
      title: 'Unpair Device?',
      text: `Are you sure you want to unpair the physical device from "${kiosk.kioskName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unpair',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const callable = httpsCallable(getFunctions(), 'unpairDevice');
        await callable({
          deviceType: 'kiosk',
          linkedDocId: kiosk.id,
        });
        await loadData(user.uid);
        Swal.fire('Unpaired', 'Device unpaired successfully', 'success');
      } catch (error) {
        console.error('Error unpairing device:', error);
        Swal.fire('Error', error.message || 'Failed to unpair device', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (kiosk) => {
    const result = await Swal.fire({
      title: 'Delete Kiosk?',
      text: `Are you sure you want to delete "${kiosk.kioskName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'vkiosks', kiosk.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Kiosk deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting kiosk:', error);
        Swal.fire('Error', 'Failed to delete kiosk', 'error');
      }
    }
  };

  const handleOpenKiosk = (kioskId) => {
    navigate(`/admin/kiosk/${kioskId}`);
  };

  const handlePairReader = async (kiosk) => {
    if (!user) return;
    const location = locations.find(l => l.id === kiosk.locationId);
    if (!location) {
      Swal.fire('Error', 'Location not found for this kiosk', 'error');
      return;
    }
    if (!location.stripeLocationId) {
      Swal.fire('Error', 'This location does not have a Stripe Terminal location linked. Please link it in the Locations page first.', 'error');
      return;
    }

    const { value: registrationCode } = await Swal.fire({
      title: 'Pair Stripe Reader',
      input: 'text',
      inputLabel: 'Enter the pairing code displayed on your BBPOS WisePOS E device',
      inputPlaceholder: 'e.g. P123-456-789',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please enter the pairing code';
        }
      }
    });

    if (!registrationCode) return;

    try {
      Swal.fire({
        title: 'Pairing Reader...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      const functions = getFunctions();
      const createStripeTerminalReaderFn = httpsCallable(functions, 'createStripeTerminalReader');
      await createStripeTerminalReaderFn({
        posId: kiosk.id,
        locationId: kiosk.locationId,
        stripeLocationId: location.stripeLocationId,
        registrationCode: registrationCode.trim(),
        label: kiosk.kioskName || 'BBPOS WisePOS E',
        deviceType: 'kiosk',
      });

      await loadData(user.uid);
      Swal.fire('Success', 'Stripe Reader paired successfully!', 'success');
    } catch (error) {
      console.error('Error pairing reader:', error);
      Swal.fire('Error', error.message || 'Failed to pair Stripe Reader', 'error');
    }
  };

  const handleUnregisterReader = async (kiosk) => {
    if (!user || !kiosk.stripeTerminalId) return;
    const result = await Swal.fire({
      title: 'Unregister Reader?',
      text: `Are you sure you want to unregister the Stripe reader from "${kiosk.kioskName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unregister',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: 'Unregistering...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });
        const functions = getFunctions();
        const deleteStripeTerminalReaderFn = httpsCallable(functions, 'deleteStripeTerminalReader');
        await deleteStripeTerminalReaderFn({
          posId: kiosk.id,
          stripeTerminalId: kiosk.stripeTerminalId,
          deviceType: 'kiosk',
        });
        await loadData(user.uid);
        Swal.fire('Success', 'Stripe Reader unregistered successfully!', 'success');
      } catch (error) {
        console.error('Error unregistering reader:', error);
        Swal.fire('Error', error.message || 'Failed to unregister Stripe Reader', 'error');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <AdminLayout title="Virtual Kiosks">
        <div className="text-center py-5">
          <img
            src="/fodyfee_loading.png"
            alt="Loading..."
            style={{ width: 80, height: 80, objectFit: 'contain' }}
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Virtual Kiosks">
      <SetupGuard prerequisites={['location']}>
      <div className="admin-account-page">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Manage your virtual self-service kiosks</h5>
              <button className="btn btn-primary" onClick={openAddModal}>
                <i className="fas fa-plus mr-2"></i>Add Kiosk
              </button>
            </div>
          </div>
        </div>

        {/* Kiosk Grid */}
        {kiosks.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-desktop fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No kiosks yet</h4>
            <p className="text-muted">Click "Add Kiosk" to create your first kiosk</p>
            <button className="btn btn-primary mt-3" onClick={openAddModal}>
              <i className="fas fa-plus mr-2"></i>Add Kiosk
            </button>
          </div>
        ) : (
          <div className="row">
            {kiosks.map((kiosk) => (
              <div key={kiosk.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-start mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          background: 'rgba(255,107,53,0.1)',
                          color: '#ff6b35',
                        }}
                      >
                        <i className="fas fa-desktop fa-lg"></i>
                      </div>
                      <div className="flex-grow-1">
                        <h5
                          className="card-title font-weight-bold mb-1"
                          style={{ color: '#ff6b35', fontSize: '1.1rem' }}
                        >
                          {kiosk.kioskName || 'Unnamed Kiosk'}
                        </h5>
                        <p
                          className="card-text text-muted mb-0"
                          style={{ fontSize: '0.85rem' }}
                        >
                          <i className="fas fa-calendar-alt mr-1"></i>
                          Created {formatDate(kiosk.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Reader status */}
                    {kiosk.stripeTerminalId && (
                      <div className="mb-2">
                        <span className="badge badge-success" style={{ fontSize: '0.75rem' }}>
                          <i className="fas fa-credit-card mr-1"></i>
                          Reader: {kiosk.stripeTerminalReaderLabel || kiosk.stripeTerminalId}
                        </span>
                      </div>
                    )}
                    {kiosk.acceptsCash && (
                      <div className="mb-2">
                        <span className="badge badge-info" style={{ fontSize: '0.75rem', backgroundColor: '#17a2b8' }}>
                          <i className="fas fa-money-bill-wave mr-1"></i>
                          Accepts Cash
                        </span>
                      </div>
                    )}
                    <div className="mb-2">
                      <span className="badge badge-light text-dark mr-1" style={{ fontSize: '0.7rem', border: '1px solid #ddd' }}>
                        <i className="fas fa-clock mr-1"></i>
                        Idle: {kiosk.idleTimeoutMinutes ?? 5}m
                      </span>
                      <span className="badge badge-light text-dark" style={{ fontSize: '0.7rem', border: '1px solid #ddd' }}>
                        <i className="fas fa-stopwatch mr-1"></i>
                        Checkout: {kiosk.checkoutTimeoutMinutes ?? 2}m
                      </span>
                      <span
                        className="badge ml-1"
                        style={{
                          fontSize: '0.7rem',
                          border: '1px solid #ddd',
                          background: (kiosk.standbyEnabled ?? true) ? 'rgba(255,107,53,0.12)' : '#f3f4f6',
                          color: (kiosk.standbyEnabled ?? true) ? '#ff6b35' : '#9ca3af',
                        }}
                        title="Standby attract screen after idle timeout"
                      >
                        <i className="fas fa-tv mr-1"></i>
                        Standby: {(kiosk.standbyEnabled ?? true) ? 'On' : 'Off'}
                      </span>
                      <DeviceOnlineBadge deviceId={kiosk.fodyfeeDeviceId} />
                    </div>

                    <div
                      className="d-flex justify-content-between align-items-center mt-3 pt-3"
                      style={{ borderTop: '1px solid #eee' }}
                    >
                      <div>
                        <span
                          className="badge badge-light"
                          style={{
                            background: 'rgba(255,107,53,0.1)',
                            color: '#ff6b35',
                          }}
                        >
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {locations.find((l) => l.id === kiosk.locationId)?.companyName ||
                            locations.find((l) => l.id === kiosk.locationId)?.locationName ||
                            'Unknown Location'}
                        </span>
                      </div>
                      <div className="d-flex">
                        <button
                          className="btn btn-sm btn-outline-primary mr-2"
                          onClick={() => openEditModal(kiosk)}
                          title="Edit"
                        >
                          <i className="fas fa-edit mr-1"></i>Edit
                        </button>
                        {!kiosk.fodyfeeDeviceId && (
                          <button
                            className="btn btn-sm btn-outline-info mr-2"
                            onClick={() => handlePairDevice(kiosk)}
                            title="Pair Physical Device"
                          >
                            <i className="fas fa-mobile-alt"></i>
                          </button>
                        )}
                        {kiosk.fodyfeeDeviceId && (
                          <button
                            className="btn btn-sm btn-outline-danger mr-2"
                            onClick={() => handleUnpairDevice(kiosk)}
                            title="Unpair Physical Device"
                          >
                            <i className="fas fa-mobile-alt mr-1"></i>Unpair
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger mr-2"
                          onClick={() => handleDelete(kiosk)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                          onClick={() => handleOpenKiosk(kiosk.id)}
                          title="Open Kiosk"
                        >
                          <i className="fas fa-external-link-alt mr-1"></i>Open
                        </button>
                      </div>
                    </div>

                    {/* Reader pairing buttons */}
                    <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f8f9fa' }}>
                      {kiosk.stripeTerminalId ? (
                        <button
                          className="btn btn-sm btn-outline-danger btn-block"
                          style={{ borderRadius: 8 }}
                          onClick={() => handleUnregisterReader(kiosk)}
                        >
                          <i className="fas fa-unlink mr-1"></i>Unregister Reader
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-success btn-block"
                          style={{ borderRadius: 8 }}
                          onClick={() => handlePairReader(kiosk)}
                        >
                          <i className="fas fa-link mr-1"></i>Pair Reader
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inline Modal */}
        {showModal && (
          <div
            className="modal d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">
                    {editingKiosk ? 'Edit Kiosk' : 'Add Kiosk'}
                  </h5>
                  <button type="button" className="close" onClick={closeModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleSave}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Kiosk Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formKioskName}
                        onChange={(e) => setFormKioskName(e.target.value)}
                        placeholder="Enter kiosk name"
                        autoFocus
                      />
                    </div>
                    <div className="form-group form-check mt-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="acceptsCashCheck"
                        checked={formAcceptsCash}
                        onChange={(e) => setFormAcceptsCash(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="acceptsCashCheck">
                        Accepts Cash Payments
                      </label>
                    </div>
                    <div className="form-group form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="standbyEnabledCheck"
                        checked={formStandbyEnabled}
                        onChange={(e) => setFormStandbyEnabled(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="standbyEnabledCheck">
                        Standby Screen
                        <small className="form-text text-muted d-block">
                          Show the "Order and Pay here" attract screen after the idle timeout
                        </small>
                      </label>
                    </div>
                    <div className="form-row mt-3">
                      <div className="form-group col-md-6">
                        <label htmlFor="idleTimeoutInput">Idle Timeout (minutes)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="idleTimeoutInput"
                          min="1"
                          max="60"
                          value={formIdleTimeout}
                          onChange={(e) => setFormIdleTimeout(e.target.value)}
                        />
                        <small className="form-text text-muted">Clear cart after inactivity on menu</small>
                      </div>
                      <div className="form-group col-md-6">
                        <label htmlFor="checkoutTimeoutInput">Checkout Timeout (minutes)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="checkoutTimeoutInput"
                          min="1"
                          max="60"
                          value={formCheckoutTimeout}
                          onChange={(e) => setFormCheckoutTimeout(e.target.value)}
                        />
                        <small className="form-text text-muted">Return to kiosk after inactivity on checkout</small>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                    >
                      {editingKiosk ? 'Update' : 'Create'} Kiosk
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

export default VkiosksPage;
